import { useNavigate } from "react-router-dom";
import { heroData } from "@/mocks/homeData";

export default function HeroSection() {
  const navigate = useNavigate();

  const handleClick = (href: string) => {
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20abstract%20textured%20background%20with%20soft%20organic%20shapes%2C%20warm%20orange%20and%20cream%20tones%2C%20subtle%20paper%20texture%20feel%2C%20minimal%20artistic%20wallpaper%2C%20no%20people%2C%20soft%20natural%20light%2C%20editorial%20abstract%20art%2C%20warm%20neutral%20palette&width=1920&height=1080&seq=hero-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover animate-hero-drift"
          style={{ minWidth: '110%', minHeight: '110%', margin: '-5%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70" />

        {/* Floating organic shapes */}
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-primary-500/10 blur-[80px] animate-float-slow pointer-events-none" />
        <div className="absolute bottom-[-5%] right-[-5%] w-[40vw] h-[40vw] rounded-full bg-accent-500/8 blur-[100px] animate-float-medium pointer-events-none" />
        <div className="absolute top-[30%] right-[10%] w-[25vw] h-[25vw] rounded-full bg-primary-400/6 blur-[60px] animate-float-fast pointer-events-none" />
        <div className="absolute bottom-[20%] left-[5%] w-[30vw] h-[30vw] rounded-full bg-secondary-400/5 blur-[70px] animate-float-slow pointer-events-none" style={{ animationDelay: '-8s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 md:px-8 pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-4xl">
          {/* Main Copy */}
          <h1 className="animate-fade-in-up animate-delay-100 font-heading text-4xl sm:text-5xl md:text-[3.25rem] lg:text-[4rem] font-bold text-background-50 leading-[1.2] tracking-tight mb-6 md:mb-8">
            {heroData.mainCopy[0]}
            <br />
            {heroData.mainCopy[1]}
          </h1>

          {/* Sub Copy */}
          <p className="animate-fade-in-up animate-delay-200 text-base md:text-lg text-background-100/90 leading-relaxed max-w-xl mb-8 md:mb-10">
            {heroData.subCopy}
          </p>

          {/* Buttons */}
          <div className="animate-fade-in-up animate-delay-300 flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10 md:mb-12">
            <button
              onClick={() => handleClick("#contact")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              DXについて相談する
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => handleClick("#services")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md border border-background-100/50 text-background-50 text-sm md:text-base font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              サービスを見る
              <i className="ri-arrow-down-line" />
            </button>
          </div>

          {/* Target Audience */}
          <div className="animate-fade-in-up animate-delay-400">
            <p className="text-xs md:text-sm text-background-100/70 mb-3 font-label">
              こんな組織を支援しています
            </p>
            <div className="flex flex-wrap gap-2 md:gap-2.5">
              {heroData.targetAudiences.map((audience) => (
                <span
                  key={audience.label}
                  className="inline-flex items-center gap-1 px-2.5 py-1 md:px-3 md:py-1.5 rounded-full bg-background-50/15 text-background-100 text-[11px] md:text-xs font-medium backdrop-blur-sm border border-background-50/10"
                >
                  <i className={`${audience.icon} text-[11px] md:text-xs`} />
                  {audience.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-fade-in animate-delay-700">
        <div className="flex flex-col items-center gap-2 text-background-100/60">
          <span className="text-[10px] tracking-wider uppercase font-label">Scroll</span>
          <div className="w-5 h-8 rounded-full border border-background-100/40 flex justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-background-100/60 animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
}